import { useCallback, useEffect } from 'react';
import { atom, useAtomValue } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import type { StoreSelectorValue } from 'components/StoreSelector/interface';
import { CategoryFilterState, UserDB } from 'lib/db';
import type { ConversationFilterAdvance, ConversationFilterAdvanceMode } from './useFilterAdvance';
import type { ConversationCategoryType } from './useConversationCategory';

export type TimeSortType = 'asc' | 'desc' | 'waitAsc' | 'waitDesc';
export type TodoFilterType = 'all' | 'unprocessed';

const defaultSort: TimeSortType = 'asc';
const defaultTodoFilter: TodoFilterType = 'all';

/**
 * 获取指定分类的筛选状态
 */
const getCategoryFilterState = async (
  category: ConversationCategoryType,
): Promise<CategoryFilterState | undefined> => {
  return UserDB.instance?.categoryFilterState.get(category);
};

/**
 * 保存指定分类的筛选状态（IndexDB）
 */
const setCategoryFilterState = async (
  category: ConversationCategoryType,
  state: Partial<CategoryFilterState>,
  mode?: ConversationFilterAdvanceMode,
): Promise<void> => {
  const existing = await getCategoryFilterState(category);
  const newState: CategoryFilterState = {
    category,
    ...existing,
    ...state,
    filterAdvanceMode: mode ? mode : existing?.filterAdvanceMode,
  };
  await UserDB.instance?.categoryFilterState.put(newState, category);
};

const defaultStoreFilter: StoreSelectorValue = {};

export const categoryFilterStateFamily = atomFamily((category: ConversationCategoryType) =>
  atom<CategoryFilterState>({
    category,
    filterAdvance: undefined,
    filterAdvanceMode: undefined,
    sort: category === 'all' ? 'asc' : defaultSort,
    todoFilter: category === 'all' ? 'all' : defaultTodoFilter,
    storeFilter: defaultStoreFilter,
  }),
);

const categoryHydratedFamily = atomFamily((_category: ConversationCategoryType) =>
  atom<boolean>(false),
);

/**
 * Hook: 从 IndexDB hydrate 到 Jotai（每个 category 仅一次）
 */
export const useHydrateCategoryFilterState = (category: ConversationCategoryType) => {
  const hydrated = useAtomValue(categoryHydratedFamily(category));

  const hydrate = useAtomCallback(
    useCallback(
      async (get, set) => {
        if (get(categoryHydratedFamily(category))) return;

        if (!UserDB.instance) {
          // DB 还未 init：不回退 localStorage，等待下次 effect 触发
          return;
        }

        try {
          const record = await getCategoryFilterState(category);
          if (record) {
            set(categoryFilterStateFamily(category), {
              category,
              filterAdvance: record.filterAdvance,
              filterAdvanceMode: record.filterAdvanceMode,
              sort: category === 'all' ? 'asc' : record.sort ?? defaultSort,
              todoFilter: category === 'all' ? 'all' : record.todoFilter ?? defaultTodoFilter,
              storeFilter: record.storeFilter ?? defaultStoreFilter,
            });
          }
        } catch (err) {
          console.warn('[categoryFilterState] hydrate failed', err);
        } finally {
          set(categoryHydratedFamily(category), true);
        }
      },
      [category],
    ),
  );

  useEffect(() => {
    if (!hydrated) {
      hydrate();
    }
  }, [hydrated, hydrate]);
};

/**
 * Hook: 获取分类感知的高级筛选状态
 */
export const useCategoryAwareFilterAdvance = (
  category: ConversationCategoryType,
): ConversationFilterAdvance | undefined => {
  return useAtomValue(categoryFilterStateFamily(category)).filterAdvance;
};

/**
 * Hook: 设置分类感知的高级筛选状态
 */
export const useSetCategoryAwareFilterAdvance = () => {
  return useAtomCallback(
    useCallback(
      async (
        get,
        set,
        category: ConversationCategoryType,
        filterAdvance: ConversationFilterAdvance | undefined,
        mode?: ConversationFilterAdvanceMode,
      ) => {
        const prev = get(categoryFilterStateFamily(category));
        const next: CategoryFilterState = {
          ...prev,
          category,
          filterAdvance,
          filterAdvanceMode: mode ?? prev.filterAdvanceMode,
        };

        set(categoryFilterStateFamily(category), next);

        try {
          await setCategoryFilterState(category, { filterAdvance }, mode);
        } catch (err) {
          console.warn('[categoryFilterState] persist filterAdvance failed', err);
        }
      },
      [],
    ),
  );
};

/**
 * Hook: 获取分类感知的筛选模式
 */
export const useCategoryAwareFilterAdvanceMode = (
  category: ConversationCategoryType,
): ConversationFilterAdvanceMode | undefined => {
  return useAtomValue(categoryFilterStateFamily(category)).filterAdvanceMode;
};

/**
 * Hook: 设置分类感知的筛选模式
 */
export const useSetCategoryAwareFilterAdvanceMode = () => {
  return useAtomCallback(
    useCallback(
      async (
        _get,
        set,
        category: ConversationCategoryType,
        filterAdvanceMode: ConversationFilterAdvanceMode | undefined,
      ) => {
        set(categoryFilterStateFamily(category), (prev) => ({
          ...prev,
          category,
          filterAdvanceMode,
        }));

        try {
          await setCategoryFilterState(category, { filterAdvanceMode });
        } catch (err) {
          console.warn('[categoryFilterState] persist filterAdvanceMode failed', err);
        }
      },
      [],
    ),
  );
};

/**
 * Hook: 获取分类感知的排序方式
 * 对于 'all' 分类，始终返回 'asc'
 */
export const categoryAwareSortFamily = atomFamily((category: ConversationCategoryType) => {
  return atom((get) => {
    const state = get(categoryFilterStateFamily(category));
    if (category === 'all') return 'asc';
    return (state.sort as TimeSortType) ?? defaultSort;
  });
});

export const useCategoryAwareSort = (category: ConversationCategoryType): TimeSortType => {
  return useAtomValue(categoryAwareSortFamily(category));
};

/**
 * Hook: 设置分类感知的排序方式
 */
export const useSetCategoryAwareSort = () => {
  return useAtomCallback(
    useCallback(async (_get, set, category: ConversationCategoryType, sort: TimeSortType) => {
      set(categoryFilterStateFamily(category), (prev) => ({
        ...prev,
        category,
        sort: category === 'all' ? 'asc' : sort,
      }));

      try {
        await setCategoryFilterState(category, { sort: category === 'all' ? 'asc' : sort });
      } catch (err) {
        console.warn('[categoryFilterState] persist sort failed', err);
      }
    }, []),
  );
};

/**
 * Hook: 获取分类感知的待办筛选
 * 对于 'all' 分类，始终返回 'all'
 */
export const useCategoryAwareTodoFilter = (category: ConversationCategoryType): TodoFilterType => {
  const state = useAtomValue(categoryFilterStateFamily(category));
  if (category === 'all') return 'all';
  return (state.todoFilter as TodoFilterType) ?? defaultTodoFilter;
};

/**
 * Hook: 设置分类感知的待办筛选
 */
export const useSetCategoryAwareTodoFilter = () => {
  return useAtomCallback(
    useCallback(
      async (_get, set, category: ConversationCategoryType, todoFilter: TodoFilterType) => {
        set(categoryFilterStateFamily(category), (prev) => ({
          ...prev,
          category,
          todoFilter: category === 'all' ? 'all' : todoFilter,
        }));

        try {
          await setCategoryFilterState(category, {
            todoFilter: category === 'all' ? 'all' : todoFilter,
          });
        } catch (err) {
          console.warn('[categoryFilterState] persist todoFilter failed', err);
        }
      },
      [],
    ),
  );
};

/**
 * Hook: 获取分类感知的店铺筛选
 */
export const useCategoryAwareStoreFilter = (
  category: ConversationCategoryType,
): StoreSelectorValue => {
  return useAtomValue(categoryFilterStateFamily(category)).storeFilter ?? defaultStoreFilter;
};

/**
 * Hook: 设置分类感知的店铺筛选
 */
export const useSetCategoryAwareStoreFilter = () => {
  return useAtomCallback(
    useCallback(
      async (_get, set, category: ConversationCategoryType, storeFilter: StoreSelectorValue) => {
        set(categoryFilterStateFamily(category), (prev) => ({
          ...prev,
          category,
          storeFilter,
        }));

        try {
          await setCategoryFilterState(category, { storeFilter });
        } catch (err) {
          console.warn('[categoryFilterState] persist storeFilter failed', err);
        }
      },
      [],
    ),
  );
};

/**
 * Hook: 清除指定分类的所有筛选状态
 */
export const useClearCategoryFilterState = () => {
  return useAtomCallback(
    useCallback(async (_get, set, category: ConversationCategoryType) => {
      set(categoryFilterStateFamily(category), {
        category,
        filterAdvance: undefined,
        filterAdvanceMode: undefined,
        sort: category === 'all' ? 'asc' : defaultSort,
        todoFilter: category === 'all' ? 'all' : defaultTodoFilter,
        storeFilter: defaultStoreFilter,
      });
      set(categoryHydratedFamily(category), true);

      try {
        await UserDB.instance?.categoryFilterState.delete(category);
      } catch (err) {
        console.warn('[categoryFilterState] clear failed', err);
      }
    }, []),
  );
};
